/**
 * Gobodo File Service
 *
 * Upload, list, and download files/attachments via gobodo-api-promise FilesApi.
 * Uses base64 upload (uploadFileBase64) and listFiles/getFileById for listing and download.
 * Accepts parentId as either a numeric object id (string) or a Gobodo object key (e.g. Client__mj_c key).
 */

import * as GobodoApi from '@gobodo/gobodo-api-promise';
import { getGobodoClient } from '../../gobodo/gobodoClient';
import { handleGobodoError, logGobodoError } from '../../utils/gobodoErrorHandler';
import type { CreditBlockFile } from '../../types';

/** Resolve parentId to numeric Gobodo object id. If it's already numeric, use it; otherwise resolve by object key. Exported for use by traversal queries (history, credit report). */
export async function resolveParentToObjectId(parentId: string): Promise<number> {
  const n = Number(parentId);
  if (Number.isFinite(n) && n >= 0) return n;
  const client = getGobodoClient();
  const objectsApi = new GobodoApi.GraphObjectsApi(client);
  const obj = await objectsApi.getObjectByKey(parentId);
  const id = obj?.id;
  if (id == null || !Number.isFinite(Number(id))) {
    throw new Error(`Could not resolve parent for file operation: "${parentId}" (object not found or invalid id)`);
  }
  return Number(id);
}

export interface UploadFileOptions {
  description?: string;
  objecttype?: number;
  objectkey?: string;
  reltypeid?: number;
  reltypekey?: string;
}

/** Read a browser File as a data URI with filename in the payload (e.g. data:image/jpeg;name=photo.jpg;base64,...) */
function fileToDataUri(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      let dataUri = reader.result as string;
      const name = file.name?.trim();
      if (name && dataUri.startsWith('data:') && dataUri.includes(';base64,')) {
        const nameParam = `name=${encodeURIComponent(name)}`;
        dataUri = dataUri.replace(';base64,', `;${nameParam};base64,`);
      }
      resolve(dataUri);
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

/** Map Gobodo API File (or raw response) to CreditBlockFile */
function mapApiFileToCreditBlockFile(raw: any): CreditBlockFile {
  const id = raw?.id != null ? String(raw.id) : '';
  const name = raw?.name ?? raw?.fileName ?? '';
  const size = typeof raw?.size === 'number' ? raw.size : 0;
  const contenttype = raw?.contenttype ?? raw?.contentType ?? raw?.fileType ?? 'application/octet-stream';
  const creationdate = raw?.creationdate ?? raw?.creationDate ?? raw?.uploadedDate;
  const dateStr =
    creationdate instanceof Date
      ? creationdate.toISOString()
      : typeof creationdate === 'string'
        ? creationdate
        : new Date().toISOString();
  return {
    id,
    fileName: name,
    fileType: contenttype,
    fileSize: size,
    uploadedBy: raw?.uploadedBy ?? raw?.createdBy ?? '',
    uploadedDate: dateStr,
    category: deriveCategory(name, contenttype),
    description: raw?.description,
    downloadUrl: raw?.downloadUrl ?? raw?.fileurl ?? raw?.url,
  };
}

/** Infer a semantic category from filename. Gobodo's FilesApi doesn't
 *  surface the reltypekey on the list response, so we fall back to filename
 *  patterns to recognise known uploaders (Array credit pulls → reports). */
function deriveCategory(fileName: string | undefined, _contentType: string | undefined): CreditBlockFile['category'] {
  const name = (fileName ?? '').toLowerCase();
  if (name.startsWith('credit-report-') || name.includes('-credit-report') || name.includes('_credit_report')) return 'reports';
  return 'other';
}

/**
 * Upload a file to a parent object (e.g. Credit Block client) via base64.
 * parentId can be the Gobodo object key (e.g. 001Nq...__mj_c) or numeric id string.
 * Converts the browser File to a data URI and calls FilesApi.uploadFileBase64.
 */
export async function uploadFile(
  parentId: string,
  file: File,
  options?: UploadFileOptions
): Promise<CreditBlockFile[]> {
  const objectId = await resolveParentToObjectId(parentId);

  const dataUri = await fileToDataUri(file);
  const originalName = file.name || 'uploaded_file';
  const base64AttachmentUpload = {
    file: dataUri,
    fileName: originalName,
    mimeType: file.type || 'application/octet-stream',
  };

  const client = getGobodoClient();
  const api = new GobodoApi.FilesApi(client);

  try {
    const response = await api.uploadFileBase64(base64AttachmentUpload, {
      objectid: objectId,
      description: options?.description,
      objecttype: options?.objecttype,
      objectkey: options?.objectkey,
      reltypeid: options?.reltypeid,
      reltypekey: options?.reltypekey,
    });

    const list = Array.isArray(response) ? response : response != null ? [response] : [];
    return list.map(mapApiFileToCreditBlockFile);
  } catch (error: any) {
    const gobodoError = handleGobodoError(error, {
      operation: 'uploadFile',
      objectId: parentId,
      fileName: file.name,
    });
    if (gobodoError) {
      logGobodoError(gobodoError);
      throw gobodoError;
    }
    throw error;
  }
}

export interface ListFilesOptions {
  pageNum?: number;
  pageSize?: number;
  relationshipTypeKey?: string[];
  relationshipTypeId?: number[];
}

/**
 * List files attached to a parent object (container).
 * parentId can be the Gobodo object key (e.g. 001Nq...__mj_c) or numeric id string.
 * Uses FilesApi.listFiles with container = numeric object id.
 * Optionally filter by relationshipTypeKey and/or relationshipTypeId.
 */
export async function listFiles(
  parentId: string,
  opts?: ListFilesOptions
): Promise<CreditBlockFile[]> {
  let container: number;
  try {
    container = await resolveParentToObjectId(parentId);
  } catch {
    return [];
  }

  const client = getGobodoClient();
  const api = new GobodoApi.FilesApi(client);

  try {
    const response = await api.listFiles({
      container,
      pageNum: opts?.pageNum ?? 0,
      pageSize: opts?.pageSize ?? 200,
      ...(opts?.relationshipTypeKey != null && opts.relationshipTypeKey.length > 0 && { relationshipTypeKey: opts.relationshipTypeKey }),
      ...(opts?.relationshipTypeId != null && opts.relationshipTypeId.length > 0 && { relationshipTypeId: opts.relationshipTypeId }),
    });

    const list = Array.isArray(response) ? response : [];
    return list.map(mapApiFileToCreditBlockFile);
  } catch (error: any) {
    const gobodoError = handleGobodoError(error, {
      operation: 'listFiles',
      objectId: parentId,
    });
    if (gobodoError) {
      logGobodoError(gobodoError);
      throw gobodoError;
    }
    throw error;
  }
}

export interface CountFilesOptions {
  relationshipTypeKey?: string[];
  relationshipTypeId?: number[];
}

/**
 * Get the count of files attached to a parent object (container), optionally filtered by relationship type(s).
 * Uses FilesApi.countFiles with the same filters as listFiles.
 * Returns 0 if parent cannot be resolved or on error.
 */
export async function countFiles(
  parentId: string,
  opts?: CountFilesOptions
): Promise<number> {
  let container: number;
  try {
    container = await resolveParentToObjectId(parentId);
  } catch {
    return 0;
  }

  const client = getGobodoClient();
  const api = new GobodoApi.FilesApi(client);

  try {
    const response = await api.countFiles({
      container,
      ...(opts?.relationshipTypeKey != null && opts.relationshipTypeKey.length > 0 && { relationshipTypeKey: opts.relationshipTypeKey }),
      ...(opts?.relationshipTypeId != null && opts.relationshipTypeId.length > 0 && { relationshipTypeId: opts.relationshipTypeId }),
    });
    if (typeof response === 'number') return response;
    if (response != null && typeof (response as any).count === 'number') return (response as any).count;
    return 0;
  } catch (error: any) {
    const gobodoError = handleGobodoError(error, {
      operation: 'countFiles',
      objectId: parentId,
    });
    if (gobodoError) {
      logGobodoError(gobodoError);
    }
    return 0;
  }
}

/**
 * Get a single file by id (metadata and optionally download).
 * Returns file metadata; if the API exposes blob/URL it can be used for download.
 */
export async function getFileById(id: number): Promise<CreditBlockFile> {
  const client = getGobodoClient();
  const api = new GobodoApi.FilesApi(client);

  try {
    const raw = await api.getFileById(id);
    return mapApiFileToCreditBlockFile(raw);
  } catch (error: any) {
    const gobodoError = handleGobodoError(error, {
      operation: 'getFileById',
      objectId: String(id),
    });
    if (gobodoError) {
      logGobodoError(gobodoError);
      throw gobodoError;
    }
    throw error;
  }
}
